import { useCallback, useEffect, useState } from 'react';
import { Box, IconButton, Tooltip, Typography, makeStyles } from '@material-ui/core';
import AssignmentIcon from '@material-ui/icons/Assignment';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import LaunchIcon from '@material-ui/icons/Launch';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import RefreshIcon from '@material-ui/icons/Refresh';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useSidebarOpenState, Link } from '@backstage/core-components';
import { Entity, ResourceEntity } from '@backstage/catalog-model';

// Fallback used only when a lease entity predates the "Access AWS Account"
// link emitted by the innovationsandbox catalog provider.
const AWS_ACCESS_PORTAL_CONSOLE_URL = 'https://plateng.awsapps.com/start/#/console';

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(1, 2),
    minWidth: 0,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  header: {
    fontSize: '0.65rem',
    textTransform: 'uppercase',
    fontWeight: 700,
    letterSpacing: '0.05em',
    color: theme.palette.common.white,
    opacity: 0.6,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  pageInfo: {
    fontSize: '0.6rem',
    color: theme.palette.common.white,
    opacity: 0.6,
  },
  refreshButton: {
    padding: 2,
    color: theme.palette.common.white,
    opacity: 0.5,
    '&:hover': {
      opacity: 1,
    },
    '&.Mui-disabled': {
      color: theme.palette.common.white,
      opacity: 0.2,
    },
  },
  '@keyframes spin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
  spinning: {
    animation: '$spin 0.8s linear infinite',
  },
  row: {
    fontSize: '0.75rem',
    color: theme.palette.common.white,
    opacity: 0.85,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  terminateLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.7rem',
    color: theme.palette.error.light,
    marginTop: theme.spacing(0.5),
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  accessLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.7rem',
    color: theme.palette.common.white,
    opacity: 0.85,
    marginTop: theme.spacing(0.5),
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
      opacity: 1,
    },
  },
  iconWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(0.5, 0),
    color: theme.palette.common.white,
    opacity: 0.7,
  },
}));

function formatDate(value: string): string {
  if (!value) return '';
  const num = Number(value);
  const date = isNaN(num) ? new Date(value) : new Date(num * 1000);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function accountAccessUrl(
  entity: ResourceEntity,
  accountId?: string,
): string | undefined {
  const fromLinks = entity.metadata.links?.find(
    link => link.title === 'Access AWS Account',
  )?.url;
  if (fromLinks) return fromLinks;
  if (accountId) {
    return `${AWS_ACCESS_PORTAL_CONSOLE_URL}?account_id=${accountId}`;
  }
  return undefined;
}

export const ActiveLeaseInfo = () => {
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const [entities, setEntities] = useState<ResourceEntity[]>([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick(t => t + 1), []);

  // Poll for lease changes every 30s so the expiry countdown and
  // active/inactive state stay current without a manual refresh.
  useEffect(() => {
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;

    async function fetchLeases() {
      setLoading(true);
      try {
        const identity = await identityApi.getBackstageIdentity();
        const response = await catalogApi.getEntities({
          filter: {
            kind: 'Resource',
            'spec.type': 'aws-sandbox-lease',
            'relations.ownedBy': identity.userEntityRef,
          },
        });
        const active = response.items.filter(
          (e: Entity): boolean =>
            e.metadata.annotations?.['sandbox.aws.com/is-active'] === 'true',
        ) as ResourceEntity[];
        if (!cancelled) {
          setEntities(active);
          // Keep the user on their current lease across auto-refreshes;
          // only clamp if the list shrank.
          setPage(p => Math.min(p, Math.max(0, active.length - 1)));
        }
      } catch {
        // Silently ignore — sidebar must not break on error
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchLeases();
    return () => {
      cancelled = true;
    };
  }, [catalogApi, identityApi, tick]);

  const entity = entities[page];
  if (!entity) return null;

  const annotations = entity.metadata.annotations ?? {};
  const accountId = annotations['sandbox.aws.com/aws-account-id'];
  const expirationDate = annotations['sandbox.aws.com/expiration-date'];
  const formattedExpiry = formatDate(expirationDate ?? '');
  const entityRef = `resource:${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}`;
  const terminateUrl = `/create/templates/default/aws-sandbox-terminate?formData=${encodeURIComponent(JSON.stringify({ leaseResource: entityRef }))}`;
  const accessUrl = accountAccessUrl(entity, accountId);
  const multiple = entities.length > 1;
  const position = multiple ? ` (${page + 1}/${entities.length})` : '';

  if (!isOpen) {
    const tooltipText = [
      accountId && `Account: ${accountId}`,
      formattedExpiry && `Expires: ${formattedExpiry}`,
      multiple && `${entities.length} active leases`,
    ]
      .filter(Boolean)
      .join(' · ');
    return (
      <Tooltip title={tooltipText || 'Active Lease'} placement="right">
        <div className={classes.iconWrapper}>
          <AssignmentIcon fontSize="small" />
        </div>
      </Tooltip>
    );
  }

  return (
    <Box className={classes.container}>
      <div className={classes.headerRow}>
        <Typography className={classes.header} variant="caption">
          My Active AWS Lease{position}
        </Typography>
        <div className={classes.headerActions}>
          {multiple && (
            <>
              <Tooltip title="Previous lease" placement="bottom">
                <span>
                  <IconButton
                    size="small"
                    className={classes.refreshButton}
                    aria-label="Previous lease"
                    disabled={page === 0}
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                  >
                    <ChevronLeftIcon style={{ fontSize: '0.95rem' }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Next lease" placement="bottom">
                <span>
                  <IconButton
                    size="small"
                    className={classes.refreshButton}
                    aria-label="Next lease"
                    disabled={page === entities.length - 1}
                    onClick={() =>
                      setPage(p => Math.min(entities.length - 1, p + 1))
                    }
                  >
                    <ChevronRightIcon style={{ fontSize: '0.95rem' }} />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
          <Tooltip title="Refresh" placement="right">
            <IconButton size="small" className={classes.refreshButton} onClick={refresh}>
              <RefreshIcon style={{ fontSize: '0.85rem' }} className={loading ? classes.spinning : undefined} />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      {accountId && (
        <Typography className={classes.row} variant="body2">
          Account: {accountId}
        </Typography>
      )}
      {formattedExpiry && (
        <Typography className={classes.row} variant="body2">
          Expires: {formattedExpiry}
        </Typography>
      )}
      {accessUrl && (
        <Link to={accessUrl} className={classes.accessLink} underline="none" externalLinkIcon={false}>
          <LaunchIcon style={{ fontSize: '0.9rem' }} />
          Access AWS Account
        </Link>
      )}
      <Link to={terminateUrl} className={classes.terminateLink} underline="none">
        <DeleteOutlineIcon style={{ fontSize: '0.9rem' }} />
        Terminate Lease
      </Link>
    </Box>
  );
};
